import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import ShareActions from "./components/ShareActions";

export default function RateTheNubbis() {
  const [characters, setCharacters] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(50);
  const [loading, setLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [topRated, setTopRated] = useState([]);
  const [averageRating, setAverageRating] = useState(null);
  const [universeName, setUniverseName] = useState(null);

  useEffect(() => {
    async function fetchCharacters() {
      const { data, error } = await supabase
        .from("characters")
        .select("id, name, image, creador_url, nubb_id, universo");

      if (error) {
        console.error("Error loading characters:", error);
        return;
      }

      const shuffled = data.sort(() => 0.5 - Math.random());
      setCharacters(shuffled);
      setLoading(false);
    }

    fetchCharacters();
  }, []);

  const current = characters[currentIndex];

  useEffect(() => {
    async function fetchCurrentAverage() {
      if (!current) return;

      const { data, error } = await supabase
        .from("nubbivotes")
        .select("score")
        .eq("character_id", current.id);

      if (error || !data) {
        setAverageRating(null);
        return;
      }

      if (data.length === 0) {
        setAverageRating("No votes yet");
      } else {
        const total = data.reduce((sum, v) => sum + v.score, 0);
        const avg = total / data.length;
        setAverageRating(avg.toFixed(1));
      }
    }

    async function fetchUniverseName() {
      if (!current || !current.universo) return;

      const { data, error } = await supabase
        .from("universes")
        .select("name")
        .eq("code", current.universo)
        .single();

      if (error) {
        setUniverseName("Unknown");
      } else {
        setUniverseName(data?.name || "Unknown");
      }
    }

    fetchCurrentAverage();
    fetchUniverseName();
  }, [current]);

  useEffect(() => {
    async function fetchRatings() {
      const { data: votesData, error } = await supabase
        .from("nubbivotes")
        .select("character_id, score");

      if (error) {
        console.error("Error loading votes:", error);
        return;
      }

      const grouped = {};

      for (const vote of votesData) {
        const id = vote.character_id;
        if (!grouped[id]) grouped[id] = [];
        grouped[id].push(vote.score);
      }

      const averages = Object.entries(grouped).map(([id, scores]) => ({
        id,
        average: scores.reduce((a, b) => a + b, 0) / scores.length,
      }));

      const sorted = averages.sort((a, b) => b.average - a.average).slice(0, 300);

      const { data: characters } = await supabase
        .from("characters")
        .select("id, name, image");

      const merged = sorted.map((entry) => {
        const char = characters.find((c) => c.id === entry.id);
        return {
          id: entry.id,
          average: entry.average.toFixed(1),
          name: char?.name || "Unknown",
          image: char?.image || "",
        };
      });

      setTopRated(merged);
    }

    fetchRatings();
  }, []);

  async function handleVote() {
    const character = characters[currentIndex];
    if (!character) return;

    const { error } = await supabase.from("nubbivotes").insert({
      character_id: character.id,
      score: parseInt(score),
    });

    if (currentIndex + 1 >= characters.length) {
      setFinished(true);
    } else {
      setCurrentIndex(currentIndex + 1);
      setScore(50);
    }
  }

  if (loading) return <p style={{ textAlign: "center", marginTop: 40 }}>Loading Nubbis...</p>;

  if (finished) {
    return (
      <div style={{ textAlign: "center", marginTop: 60 }}>
        <h1 style={{ fontSize: "2rem", fontWeight: "bold" }}>You've rated all available Nubbis!</h1>
        <p style={{ fontSize: "1.2rem" }}>Thanks for your help!</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
        <h1 style={{ fontSize: "2rem", fontWeight: "bold", marginBottom: 20 }}>Rate this Nubbi</h1>

        <img
          src={current.image}
          alt={current.name}
          style={{ width: "100%", borderRadius: 20, marginBottom: 10 }}
        />
        <h2 style={{ fontSize: "1.5rem", marginBottom: 10 }}>{current.name}</h2>

        <div style={{ marginBottom: 20, fontSize: "1rem", textAlign: "left" }}>
          <p><strong>Created by:</strong> {current.creador_url || "Unknown"}</p>
          <p><strong>Nubb-ID:</strong> {current.nubb_id || "N/A"}</p>
          <p><strong>Universe:</strong> {universeName ?? "Loading..."}</p>
          <p><strong>Average rating:</strong> {averageRating ?? "Loading..."}</p>
        </div>

        <p style={{ fontSize: "1.2rem" }}>Your score: <strong>{score}</strong></p>
        <input
          type="range"
          min="1"
          max="100"
          value={score}
          onChange={(e) => setScore(e.target.value)}
          style={{ width: "100%", marginBottom: 20 }}
        />

        <button
          onClick={handleVote}
          style={{
            backgroundColor: "#4f46e5",
            color: "#fff",
            padding: "10px 20px",
            borderRadius: 10,
            fontSize: "1rem",
            border: "none",
            cursor: "pointer",
          }}
        >
          Submit & Next
        </button>
        <ShareActions
  name={current.name}
  id={current.id}
  image={current.image}
  creator={current.creador_url}
/>
      </div>

      <div style={{ marginTop: 60 }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: 20, textAlign: "center" }}>
          Top 100 Rated Nubbis
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 20 }}>
          {topRated.map((char, index) => (
            <div key={char.id} style={{ textAlign: "center" }}>
              <img src={char.image} alt={char.name} style={{ width: "100%", borderRadius: 12 }} />
              <p style={{ marginTop: 6, fontWeight: "bold" }}>#{index + 1} {char.name}</p>
              <p style={{ color: "#4f46e5" }}>{char.average}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}